import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErrMsg(""); setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      setErrMsg("Could not send the request — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900" data-testid="forgot-password-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3"><Link to="/"><Brand variant="dark" size={18} /></Link></div>
      </header>
      <main className="max-w-md mx-auto px-4 py-12">
        {sent ? (
          <div className="p-6 rounded-lg border border-emerald-300 bg-emerald-50" data-testid="forgot-sent">
            <MailCheck className="w-6 h-6 text-emerald-700 mb-3" />
            <h1 className="text-xl font-bold text-emerald-900">Check your email</h1>
            <p className="text-sm text-emerald-900 mt-2 leading-relaxed">If an account exists for <span className="font-semibold">{email}</span>, a reset link is on its way. It works one time and expires in 60 minutes.</p>
            <Link to="/login" className="mt-4 inline-flex items-center text-sm font-semibold underline text-emerald-900" data-testid="forgot-back-login">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 rounded-lg border border-slate-200 bg-white space-y-4" data-testid="forgot-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Account recovery</div>
              <h1 className="text-2xl font-bold mt-1 tracking-[-0.01em]">Reset your password</h1>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">Enter the email on your Throughline account. We&apos;ll send a single-use link to choose a new password.</p>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Email</span>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} autoComplete="email"
                     className="w-full mt-1 border border-slate-300 rounded-md px-3 py-3 text-base min-h-[48px] bg-white" data-testid="forgot-email" />
            </label>
            {errMsg && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="forgot-err">{errMsg}</div>}
            <button type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-md py-3 min-h-[48px]" data-testid="forgot-submit">
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <Link to="/login" className="inline-flex items-center text-sm text-slate-600 underline" data-testid="forgot-cancel">Back to sign in</Link>
          </form>
        )}
      </main>
    </div>
  );
}
